import type { ReactNode } from 'react';

import { ParrotLogo } from '@experimental-components/IconsComponents/ParrotLogo';
import { WizardBackground } from '@identity/presentation/components/base/WizardBackground';

export type IdentityWizardLayoutProps = {
  children: ReactNode;
};

/**
 * Two-column frame shared by the flows that show no progress: photograph on the left, card on the
 * right, and the photograph hidden below `lg` with nothing in its place.
 *
 * It shows no progress at any width, and that is not a responsive omission: nobody chose to start
 * these flows, and counting the steps of something they could not refuse only makes it feel longer.
 *
 * It is a frame of its own rather than the invitation layout with flags. The two share the card slot
 * and nothing else, so one component would need three optional props and three branches to save a
 * `<div>`.
 *
 * @param {IdentityWizardLayoutProps} props - Component properties
 */
export const IdentityWizardLayout = ({ children }: IdentityWizardLayoutProps) => (
  <div className="flex min-h-screen w-full font-sans">
    <aside
      className="relative hidden h-auto w-[56%] shrink-0 overflow-hidden lg:block"
      data-testid="identity-wizard-layout-panel"
    >
      <WizardBackground />
    </aside>

    <main className="flex flex-1 flex-col items-center justify-center gap-6 px-4 py-6 md:px-16 md:py-10">
      <ParrotLogo aria-hidden className="h-16 self-start" />
      {children}
    </main>
  </div>
);
